import type { EditorAiCreditMeter } from 'definitions/editor-ai-ui';

/**
 * How AI credits read, wherever an AI chat shows them: the editor's panel and top bar, and the
 * dashboard's results chat (P-PER-44). One ledger and one monthly cap serve both, so the meter
 * must read the same in both. Credits, never tokens, everywhere (decision 1 of 2026-10-16).
 */

/** The copy is English, and its numbers read the same way everywhere. */
const LOCALE = 'en-US';
const WHOLE = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const TENTHS = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** "14 credits", "1 credit", "<1 credit". A turn that cost nothing reads "0 credits". */
export const formatCredits = (credits: number): string => {
  if (credits > 0 && credits < 0.5) return '<1 credit';
  const whole = credits > 0 ? Math.max(1, Math.round(credits)) : 0;
  return `${WHOLE.format(whole)} ${whole === 1 ? 'credit' : 'credits'}`;
};

export const formatNumber = (value: number): string => WHOLE.format(Math.round(value));

/** From here the meter's number is written in thousands. */
const COMPACT_FROM = 10_000;

/**
 * The top bar meter's number, credits left (ai-topbar handoff 3): "620", "1,240", then "12.4k" from
 * 10,000. Rounded down, so it never shows a credit that is not there, and a sliver reads "<1"
 * rather than an empty-looking "0" while a turn can still be sent.
 */
export const formatCreditsLeft = (credits: number): string => {
  const left = Math.max(0, credits);
  if (left > 0 && left < 1) return '<1';
  if (left < COMPACT_FROM) return WHOLE.format(Math.floor(left));
  return `${TENTHS.format(Math.floor(left / 100) / 10)}k`;
};

/** normal from 10% left, low below it, empty at nothing left (the server refuses the next turn). */
export type CreditLevel = 'normal' | 'low' | 'empty';

/** The share of the allotment left below which the meter turns low. */
export const LOW_BELOW_LEFT = 0.1;

type CreditLevelParams = { remaining: number; allotment: number };

export const creditLevel = ({ remaining, allotment }: CreditLevelParams): CreditLevel => {
  if (remaining <= 0) return 'empty';
  return allotment > 0 && remaining / allotment < LOW_BELOW_LEFT ? 'low' : 'normal';
};

export type MeterView = {
  /** Credits that can be spent now: the month's included credits left, plus credit packs. */
  remaining: number;
  allotment: number;
  level: CreditLevel;
  /** The fill, 0 to 100: remaining against the allotment, clamped. */
  percent: number;
  /** The meter's number: "620", "12.4k". */
  value: string;
  /** "620 of 1,000 credits left", for the popover and the panel. */
  line: string;
  /** "620 of 1,000 credits left this month", the tooltip. */
  tooltip: string;
  /** "Credits: 620 of 1,000 left this month", for aria-label. */
  label: string;
  /** Low or empty: the meter and the panel take the warning colour, and the announcer warns. */
  warn: boolean;
  /** Nothing left to spend: the server refuses the next turn with a 402, so send is disabled. */
  exhausted: boolean;
};

/**
 * The meter reads credits left, and its fill and number always agree. Left is the spendable
 * balance (included plus packs), the same figure the server gates on, so the meter is empty
 * exactly when a turn would be refused. Packs can take it past the allotment: the fill is then
 * full and the words stop saying "of". With no allotment at all (a card account with no ledger,
 * or an agency's own) there is no "of" either, and nothing about packs unless there are some.
 */
export const meterView = (meter: Readonly<EditorAiCreditMeter>): MeterView => {
  const { allotment } = meter;
  const remaining = Math.max(0, meter.balance);
  const level = creditLevel({ remaining, allotment });
  const share = allotment > 0 ? remaining / allotment : remaining > 0 ? 1 : 0;
  const percent = Math.round(Math.min(1, Math.max(0, share)) * 1000) / 10;
  const left = formatNumber(Math.floor(remaining));
  const ofAllotment = allotment > 0 && remaining <= allotment;
  const withPacks = !ofAllotment && meter.packRemaining > 0;
  const amount = ofAllotment ? `${left} of ${formatNumber(allotment)}` : left;
  const line = withPacks ? `${left} credits left, with credit packs` : `${amount} credits left`;
  return {
    remaining,
    allotment,
    level,
    percent,
    value: formatCreditsLeft(remaining),
    line,
    tooltip: ofAllotment ? `${line} this month` : line,
    label: withPacks
      ? `Credits: ${left} left, with credit packs`
      : `Credits: ${amount} left${ofAllotment ? ' this month' : ''}`,
    warn: level !== 'normal',
    exhausted: level === 'empty',
  };
};

const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' });

/** "1 November", or null. */
export const formatResetDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : DATE.format(date);
};
